// ToastContext.tsx
import React, { createContext, useContext, useState, useCallback } from "react";
import { IToastProps } from "./Toast";
import Toast from "./Toast";
import { generateId } from "./utils/utils";

type ToastWithId = IToastProps & { id: string };

interface ToastContextType {
  showToast: (toast: Omit<IToastProps, "show">) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastWithId[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback((toast: Omit<IToastProps, "show">) => {
    const id = generateId();
    const newToast: ToastWithId = {
      ...toast,
      id,
      show: true,
      onClose: () => removeToast(id),
    };
    setToasts((prev) => [...prev, newToast]);
  }, [removeToast]);

  const positions = ["top-left", "top-right", "bottom-left", "bottom-right"] as const;

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      {positions.map((position) => (
        <div key={position} className={`common_toast_container common_toast_container--${position}`}>
          {toasts
            .filter((t) => t.position === position)
            .map((toast) => (
              <Toast key={toast.id} {...toast} />
            ))}
        </div>
      ))}
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextType => {
  const context = useContext(ToastContext);
  if (!context) throw new Error("useToast must be used within a ToastProvider");
  return context;
};


export default ToastProvider;
